import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useEffect } from "react";
import { useCriterion, useUpdateCriterion } from "@/api/criteria";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Schema = z.object({
  name: z.string().min(1, "Required"),
  target_titles: z.string().optional(),
  keywords: z.string().optional(),
  locations: z.string().optional(),
  seniority: z.string().optional(),
  description: z.string().optional(),
  min_fit_score: z.coerce.number().int().min(0).max(100),
});
type Form = z.infer<typeof Schema>;

export function CriterionEdit() {
  const { criterionId } = useParams({ from: "/criteria/$criterionId/edit" });
  const id = Number(criterionId);
  const navigate = useNavigate();
  const { data, isLoading } = useCriterion(id);
  const update = useUpdateCriterion(id);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: { min_fit_score: 60 },
  });

  useEffect(() => {
    document.title = "Edit Criterion — Beacon";
    return () => { document.title = "Beacon"; };
  }, []);

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        target_titles: data.target_titles,
        keywords: data.keywords,
        locations: data.locations,
        seniority: data.seniority,
        description: data.description,
        min_fit_score: data.min_fit_score,
      });
    }
  }, [data, reset]);

  const onSubmit = (values: Form) =>
    update.mutate(values, {
      onSuccess: () => {
        toast.success("Saved");
        navigate({ to: "/criteria" });
      },
      onError: (e) => toast.error(`Couldn't save: ${(e as Error).message}`),
    });

  if (isLoading) {
    return <div className="h-32 animate-pulse rounded-lg bg-muted/40" />;
  }

  return (
    <div className="space-y-6">
      <Link to="/criteria" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> Back to criteria
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>Edit criterion</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" required error={errors.name?.message}>
              <Input {...register("name")} />
            </Field>
            <Field label="Target titles">
              <Input {...register("target_titles")} />
            </Field>
            <Field label="Keywords">
              <Input {...register("keywords")} />
            </Field>
            <Field label="Locations">
              <Input {...register("locations")} />
            </Field>
            <Field label="Seniority">
              <Input {...register("seniority")} />
            </Field>
            <Field label="Min fit score">
              <Input type="number" min={0} max={100} step={5} {...register("min_fit_score")} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="What you're looking for">
                <Textarea rows={4} {...register("description")} />
              </Field>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save changes"}
              </Button>
              <Button asChild variant="outline" type="button">
                <Link to="/criteria">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  required,
  children,
  error,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="text-primary"> *</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
